import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import { login as authLogin, logout as authLogout, fetchCurrentUser, refreshAuthToken as authRefreshToken } from '../services/auth';
import toast from 'react-hot-toast';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isCheckingAuth: false,
      isLoggingOut: false,

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken, isAuthenticated: true });
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      },

      setUser: (user) => {
        console.log('setUser', user);
        set({ user });
      },

      refreshUserData: async () => {
        try {
          const userData = await fetchCurrentUser();
          console.log('refreshUserData user response:', userData);
          
          get().setUser(userData);
          return true;
        } catch (error) {
          console.error('Failed to refresh user data:', error);
          return false;
        }
      },

      login: async (email, password, deviceName = '') => {
        try {
          const { access_token, refresh_token } = await authLogin(email, password, deviceName);
          get().setTokens(access_token, refresh_token);
          
          // Get user info
          const userData = await fetchCurrentUser();
          get().setUser(userData);
          
          toast.success('Login successful!');
          return true;
        } catch (error) {
          // Error handling is done in the page component
          throw error;
        }
      },

      register: async (email, password) => {
        try {
          const response = await api.post('/api/auth/register', {
            email,
            password,
          });

          // Interceptor handles unified structure, response.data is already unwrapped
          const { access_token, refresh_token } = response.data;
          get().setTokens(access_token, refresh_token);
          
          // Get user info
          const userData = await fetchCurrentUser();
          get().setUser(userData);
          
          toast.success('Registration successful!');
          return true;
        } catch (error) {
          // Error handling is done in the page component
          throw error;
        }
      },

      logout: async () => {
        // Prevent recursive logout calls
        if (get().isLoggingOut) {
          return;
        }
        
        set({ isLoggingOut: true });
        try {
          await authLogout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoggingOut: false,
          });
          delete api.defaults.headers.common['Authorization'];
          toast.success('Logged out successfully');
        }
      },


      checkAuth: async () => {
        const { accessToken } = get();
        console.log('checkAuth called, accessToken exists:', !!accessToken);
        if (accessToken) {
          set({ isCheckingAuth: true });
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          
          // Validate the token by making a request to get user info
          try {
            console.log('Validating token...');
            const userData = await fetchCurrentUser();
            console.log('Token validation successful, user data:', userData);
            // Token is valid, set authenticated and user data
            set({ isAuthenticated: true });
            get().setUser(userData);
          } catch (error) {
            console.log('Token validation failed:', error.response?.status);
            if (error.response?.status === 401) {
              // Token is invalid, try to refresh
              console.log('Attempting token refresh...');
              const success = await get().refreshAuthToken();
              if (!success) {
                console.log('Token refresh failed, clearing auth state');
                // Refresh failed, clear auth state
                set({
                  user: null,
                  accessToken: null,
                  refreshToken: null,
                  isAuthenticated: false,
                });
                delete api.defaults.headers.common['Authorization'];
              } else {
                console.log('Token refresh succeeded');
                // Refresh succeeded, set authenticated
                set({ isAuthenticated: true });
              }
            } else {
              console.log('Other error, clearing auth state');
              // Other errors, clear auth state
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
              });
              delete api.defaults.headers.common['Authorization'];
            }
          } finally {
            set({ isCheckingAuth: false });
          }
        } else {
          console.log('No access token, setting not authenticated');
          set({ isCheckingAuth: false, isAuthenticated: false });
        }
      },

      refreshAuthToken: async () => {
        try {
          const { refreshToken } = get();
          if (!refreshToken) throw new Error('No refresh token');

          const { access_token, refresh_token } = await authRefreshToken(refreshToken);
          get().setTokens(access_token, refresh_token);
          return true;
        } catch (error) {
          console.error('Refresh token failed:', error);
          // Don't call logout here to avoid infinite loops
          // The calling function will handle the logout if needed
          return false;
        }
      },

      fetchUserInfo: async () => {
        try {
          const userData = await fetchCurrentUser();
          get().setUser(userData);
          return userData;
        } catch (error) {
          console.error('Failed to fetch user info:', error);
          return null;
        }
      },

      initializeAuth: () => {
        const { accessToken, isCheckingAuth } = get();
        if (accessToken && !isCheckingAuth) {
          // If we have a token but haven't checked auth yet, start checking
          set({ isCheckingAuth: true });
          get().checkAuth();
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // When store is rehydrated, if we have a token but haven't checked auth yet,
        // set isCheckingAuth to true to prevent premature redirects
        if (state?.accessToken && !state?.isCheckingAuth) {
          state.isCheckingAuth = true;
          // Start the auth check
          setTimeout(() => {
            state.checkAuth?.();
          }, 0);
        }
      },
    }
  )
);

export { useAuthStore }; 